import React, { useEffect, useRef, useMemo } from "react";
import * as d3 from "d3";
import COLOR_ORDINAL from "../COLOR_ORDINAL";
import styles from "./styles.module.scss";
import STATISTIC_TYPES, { FIND_STATISTIC_TYPE_BY_VALUE } from "../STATISTIC_TYPES";
import ChartNote from "../ChartNote";
import formatNumber from "../../utils/formatNumber";

const MOCK_DATA = [
    {
        name: null,
        data: [
            {
                name: "沒有",
                value: "0.9329",
            },
            {
                name: "有",
                value: "0.0546",
            },
            {
                name: "不記得",
                value: "0.0125",
            },
        ],
    },
];

function LineChart({
    data = MOCK_DATA,
    statisticsType = 1,
    chartTitle = "",
    chartNote = "",
}) {
    const svgRef = useRef(null);


    useEffect(() => {
        if (!data || data.length === 0) return;

        const chartData = data[0].data;

        // 清除舊的圖表
        d3.select(svgRef.current).selectAll("*").remove();

        // 設定圖表尺寸和邊距
        const margin = { top: 20, right: 30, bottom: chartData.length > 10 ? 100 : 40, left: 60 };
        const width =
            Math.min(window.innerWidth, 1440) -
            180 -
            180 -
            margin.left -
            margin.right;
        const height = 600 - margin.top - margin.bottom;

        // 創建 SVG
        const svg = d3
            .select(svgRef.current)
            .attr("width", width + margin.left + margin.right)
            .attr("height", height + margin.top + margin.bottom)
            .append("g")
            .attr("transform", `translate(${margin.left},${margin.top})`);

        // 設定 x 軸比例尺
        const x = d3
            .scalePoint()
            .domain(chartData.map((d) => d.name))
            .range([0, width])
            .padding(0.5);

        // 設定 y 軸比例尺
        const y = d3
            .scaleLinear()
            .domain([
                0,
                statisticsType === STATISTIC_TYPES.百分比.value
                    ? 1
                    : d3.max(chartData, (d) => parseFloat(d.value)),
            ])
            .range([height, 0])
            .nice();

        // 添加剪裁路徑
        svg.append("defs")
            .append("clipPath")
            .attr("id", "chart-area")
            .append("rect")
            .attr("x", 0)
            .attr("y", 0)
            .attr("width", width)
            .attr("height", height);

        // 創建一個群組來包含背景條紋
        const chartArea = svg.append("g")
            .attr("clip-path", "url(#chart-area)");

        // 獲取 y 軸的刻度值
        const yTicks = statisticsType === STATISTIC_TYPES.百分比.value ? y.ticks(10) : y.ticks();

        // 添加水平背景條紋
        chartArea.selectAll(".background-stripe")
            .data(yTicks)
            .join("rect")
            .attr("class", "background-stripe")
            .attr("x", 0)
            .attr("y", (d, i) => y(yTicks[i]))
            .attr("width", width)
            .attr("height", (d, i) => {
                if (i === yTicks.length - 1) return 0;
                return y(yTicks[i]) - y(yTicks[i + 1]);
            })
            .attr("fill", (d, i) => (i % 2 === 0 ? "#F9F9F9" : "#fff"))
            .attr("opacity", 0.4)
            .attr("stroke", "#D2D2D2")
            .attr("stroke-width", 1);

        // 創建折線生成器
        const line = d3
            .line()
            .x((d) => x(d.name))
            .y((d) => y(parseFloat(d.value)));

        // 繪製折線
        svg.append("path")
            .datum(chartData)
            .attr("fill", "none")
            .attr("stroke", COLOR_ORDINAL[0])
            .attr("stroke-width", 2)
            .attr("d", line);

        // 繪製資料點
        svg.selectAll(".dot")
            .data(chartData)
            .join("circle")
            .attr("class", "dot")
            .attr("cx", (d) => x(d.name))
            .attr("cy", (d) => y(parseFloat(d.value)))
            .attr("r", 5)
            .attr("fill", COLOR_ORDINAL[0]);

        // 添加數值標籤
        svg.selectAll(".value-label")
            .data(chartData)
            .join("text")
            .attr("class", "value-label")
            .attr("x", (d) => x(d.name))
            .attr("y", (d) => y(parseFloat(d.value)) - 10)
            .attr("text-anchor", "middle")
            .style("font-size", "14px")
            .text((d) =>
                `${formatNumber(d.value, statisticsType)}`
            );

        // 添加 x 軸
        svg.append("g")
            .attr("transform", `translate(0,${height})`)
            .call(d3.axisBottom(x))
            .selectAll("text")
            .style("text-anchor", chartData.length > 10 ? "end" : "middle") 
            .style("font-size", chartData.length > 10 ? "12px" : "14px")
            .attr("transform", chartData.length > 10 ? "translate(0, 10) rotate(-25)" : "translate(0, 10) rotate(0)")
            .each(function(d) {
                const text = d3.select(this);
                if (d.includes('年') && d.includes('(')) {
                    // 處理 "25~33年(民國79~87年興建)" 格式
                    const [yearPart, bracketPart] = d.split('(');
                    text.text('')
                        .append('tspan')
                        .text(yearPart)
                        .attr('x', 0)
                        .attr('dy', 0);
                    text.append('tspan')
                        .text('(' + bracketPart)
                        .attr('x', 0)
                        .attr('dy', '1.2em');
                } else if (d.includes('元') && d.includes('~')) {
                    // 處理 "4千元以上~未滿5千元" 格式
                    const [firstPart, secondPart] = d.split('~');
                    text.text('')
                        .append('tspan')
                        .text(firstPart + '~')
                        .attr('x', 0)
                        .attr('dy', 0);
                    text.append('tspan')
                        .text(secondPart)
                        .attr('x', 0)
                        .attr('dy', '1.2em');
                } else if (d.includes('【訪員請紀錄】')) {
                    // 處理 "2萬元以上，約萬元【訪員請紀錄】" 格式
                    const [mainPart, note] = d.split('【訪員請紀錄】');
                    text.text('')
                        .append('tspan')
                        .text(mainPart)
                        .attr('x', 0)
                        .attr('dy', 0);
                    text.append('tspan')
                        .text('【訪員請紀錄】' + note)
                        .attr('x', 0)
                        .attr('dy', '1.2em');
                } else {
                    text.text(d);
                }

            });

        // 添加 y 軸
        svg.append("g")
            .call(d3.axisLeft(y).ticks(10).tickFormat(d => {
                if (statisticsType === STATISTIC_TYPES.百分比.value) {
                    return d3.format(".0%")(d);
                }
                if (d >= 1000) {
                    return d3.format(",")(Math.round(d));
                }
                if (d < 1) {
                    return d3.format(".2f")(d);
                }
                return d3.format(".0f")(d);
            }))
            .selectAll("text")
            .style("font-size", "14px");

    }, [data, statisticsType]);

    return (
        <div className={`${styles.lineChartContainer} line-chart`}>
            <div className={styles.lineChart}>
                <svg ref={svgRef}></svg>
            </div>
            {chartNote && <ChartNote chartNote={chartNote} />}
        </div>
    );
}

export default LineChart;
